import React, { useState } from "react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Sections";
import { Reveal, TechLabel } from "@/components/Reveal";
import { Field, TextArea } from "@/components/Field";
import { FormSuccess } from "@/components/FormSuccess";
import { CtaPrimary } from "@/components/CTA";

const RESPONSE = ["Availability", "Origin", "Lead time", "Pricing", "Delivery options"];

export default function Contact() {
  useSEO({ title: "Contact BlackSilex | Mining Parts Supplier Canada", description: "Contact BlackSilex, a mining equipment spare parts supplier in Ontario, Canada. Send the part number or equipment model and we respond with origin, lead time and pricing.", path: "/contact" });
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
        eyebrow="Contact"
        titleLines={["Let's talk about", "what is wearing out."]}
      />

      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-mutedgray text-lg leading-relaxed">
              Send us the part number, equipment model or simply describe the problem. We will work back with:
            </p>
          </Reveal>
          <ul className="mt-8 border-t border-offwhite/10">
            {RESPONSE.map((r, i) => (
              <Reveal key={r} delay={i * 0.06}>
                <li className="flex items-center gap-4 border-b border-offwhite/10 py-4" data-testid={`response-${i}`}>
                  <span className="font-mono-tech text-xs text-strike">0{i + 1}</span>
                  <span className="font-display text-lg text-offwhite">{r}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <div className="mt-12 space-y-8">
            <div>
              <TechLabel>Email</TechLabel>
              <a href="mailto:info@blacksilex.com" className="block font-display text-2xl font-bold text-offwhite mt-3 link-underline" data-testid="contact-email">info@blacksilex.com</a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <FormSuccess title="MESSAGE RECEIVED" copy="Thank you. Our team will review your message and respond with availability, origin, lead time, pricing and delivery options." />
          ) : (
            <Reveal>
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-6" data-testid="contact-form">
                <Field half label="Name" name="name" value={form.name} onChange={set} required testId="c-name" placeholder="Contact name" />
                <Field half label="Email" name="email" type="email" value={form.email} onChange={set} required testId="c-email" placeholder="you@company.com" />
                <Field half label="Company" name="company" value={form.company} onChange={set} testId="c-company" placeholder="Operation / company" />
                <Field half label="Phone" name="phone" value={form.phone} onChange={set} testId="c-phone" placeholder="Direct line" />
                <TextArea label="Message" name="message" value={form.message} onChange={set} required rows={6} testId="c-message" placeholder="Part number, equipment model, or describe the problem…" />
                <div className="md:col-span-2 pt-4">
                  <CtaPrimary type="submit" testId="c-submit">{busy ? "Sending…" : "Send Message"}</CtaPrimary>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
